import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart } from 'recharts';
export function Dashboard() {
  // Extended data for 3 years (36 months)
  const generateMonthlyData = () => {
    const years = ['2021', '2022', '2023'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return years.flatMap(year => months.map((month, idx) => {
      // Create some seasonal patterns
      const seasonalFactor = Math.sin(idx / 11 * Math.PI) * 0.3 + 1;
      const yearFactor = year === '2021' ? 0.8 : year === '2022' ? 1 : 1.2;
      const randomFactor = 0.8 + Math.random() * 0.4;
      const basePremium = 3000 * seasonalFactor * yearFactor * randomFactor;
      const baseClaimRatio = 0.4 + Math.sin(idx / 5.5 * Math.PI) * 0.1;
      return {
        date: `${month} ${year}`,
        month: month,
        year: year,
        premium: Math.round(basePremium),
        claims: Math.round(basePremium * baseClaimRatio),
        lossRatio: Math.round(baseClaimRatio * 100)
      };
    }));
  };
  const combinedData = generateMonthlyData();
  // Create heatmap data for seasonality
  const createHeatmapData = () => {
    const years = ['2021', '2022', '2023'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // Generate a matrix of values for the heatmap
    return months.map(month => {
      const monthData = {
        month
      };
      years.forEach(year => {
        const dataPoint = combinedData.find(d => d.month === month && d.year === year);
        monthData[year] = dataPoint ? dataPoint.lossRatio : 0;
      });
      return monthData;
    });
  };
  const heatmapData = createHeatmapData();
  const pieData = [{
    name: 'Carrier',
    value: 60
  }, {
    name: 'Broker',
    value: 25
  }, {
    name: 'MGA',
    value: 15
  }];
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];
  // For the heatmap color scale
  const getHeatmapColor = value => {
    // Color scale from green (low) to red (high)
    if (value < 30) return '#10B981';
    if (value < 40) return '#34D399';
    if (value < 50) return '#FBBF24';
    if (value < 60) return '#F59E0B';
    if (value < 70) return '#F97316';
    return '#EF4444';
  };
  return <div className="space-y-6" data-id="element-260">
      <div className="bg-white rounded-lg shadow p-6" data-id="element-261">
        <h2 className="text-xl font-semibold mb-6" data-id="element-262">Performance Overview</h2>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-263">
          <div className="bg-blue-50 p-4 rounded-lg" data-id="element-264">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-265">Total Premium</h3>
            <p className="text-2xl font-bold" data-id="element-266">£1,245,890</p>
            <p className="text-sm text-green-600" data-id="element-267">+12.5% from last month</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg" data-id="element-268">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-269">Total Claims</h3>
            <p className="text-2xl font-bold" data-id="element-270">£498,230</p>
            <p className="text-sm text-red-600" data-id="element-271">+3.2% from last month</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg" data-id="element-272">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-273">Loss Ratio</h3>
            <p className="text-2xl font-bold" data-id="element-274">39.9%</p>
            <p className="text-sm text-green-600" data-id="element-275">-2.1% from last month</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6" data-id="element-276">
        <h2 className="text-lg font-semibold mb-4" data-id="element-277">Time Period Analysis</h2>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-278">
          <div data-id="element-279">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-280">
              Date Range
            </label>
            <div className="flex space-x-2" data-id="element-281">
              <select className="border rounded p-2 w-full" data-id="element-282">
                <option data-id="element-283">Last 30 days</option>
                <option data-id="element-284">Last 90 days</option>
                <option data-id="element-285">Year to date</option>
                <option data-id="element-286">Last 12 months</option>
                <option selected data-id="element-287">3-Year History</option>
                <option data-id="element-288">Custom range</option>
              </select>
            </div>
          </div>
          <div data-id="element-289">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-290">
              Product Line
            </label>
            <select className="border rounded p-2 w-full" data-id="element-291">
              <option data-id="element-292">All Products</option>
              <option data-id="element-293">Life</option>
              <option data-id="element-294">P&C</option>
              <option data-id="element-295">Motor</option>
              <option data-id="element-296">Specialty</option>
            </select>
          </div>
          <div data-id="element-297">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-298">
              Distribution Channel
            </label>
            <select className="border rounded p-2 w-full" data-id="element-299">
              <option data-id="element-300">All Channels</option>
              <option data-id="element-301">Direct</option>
              <option data-id="element-302">Broker A</option>
              <option data-id="element-303">Broker B</option>
              <option data-id="element-304">Broker C</option>
            </select>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6" data-id="element-305">
        <h2 className="text-lg font-semibold mb-4" data-id="element-306">
          Premium & Claims Trend (3-Year History)
        </h2>
        <div className="h-96" data-id="element-307">
          <ResponsiveContainer width="100%" height="100%" data-id="element-308">
            <ComposedChart data={combinedData} data-id="element-309">
              <CartesianGrid strokeDasharray="3 3" data-id="element-310" />
              <XAxis dataKey="date" tick={{
              fontSize: 12
            }} interval={2} angle={-45} textAnchor="end" data-id="element-311" />
              <YAxis yAxisId="left" orientation="left" data-id="element-312" />
              <YAxis yAxisId="right" orientation="right" data-id="element-313" />
              <Tooltip formatter={(value, name) => [`£${value.toLocaleString()}`, name === 'premium' ? 'Premium' : 'Claims']} data-id="element-314" />
              <Legend data-id="element-315" />
              <Bar yAxisId="left" dataKey="claims" fill="#F97316" name="Claims" barSize={20} data-id="element-316" />
              <Line yAxisId="right" type="monotone" dataKey="premium" stroke="#3B82F6" name="Premium" strokeWidth={3} dot={{
              r: 1
            }} activeDot={{
              r: 6
            }} data-id="element-317" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-318">
        <div className="bg-white rounded-lg shadow p-6" data-id="element-319">
          <h2 className="text-lg font-semibold mb-4" data-id="element-320">Loss Ratio Seasonality</h2>
          <div className="h-80" data-id="element-321">
            <div className="flex justify-center mb-4" data-id="element-322">
              <div className="flex items-center space-x-2" data-id="element-323">
                <span className="text-xs" data-id="element-324">Low</span>
                <div className="flex h-4" data-id="element-325">
                  {[30, 40, 50, 60, 70].map((value, i) => <div key={i} className="w-8 h-4" style={{
                  backgroundColor: getHeatmapColor(value)
                }} data-id="element-326" />)}
                </div>
                <span className="text-xs" data-id="element-327">High</span>
              </div>
            </div>
            <div className="flex" data-id="element-328">
              <div className="w-16 pt-8" data-id="element-329">
                {heatmapData.map((item, index) => <div key={index} className="h-10 flex items-center justify-end pr-2 text-sm font-medium text-gray-700" data-id="element-330">
                    {item.month}
                  </div>)}
              </div>
              <div className="flex-1" data-id="element-331">
                <div className="flex h-8 mb-1" data-id="element-332">
                  {['2021', '2022', '2023'].map(year => <div key={year} className="flex-1 text-center text-sm font-medium text-gray-700" data-id="element-333">
                      {year}
                    </div>)}
                </div>
                {heatmapData.map((row, rowIndex) => <div key={rowIndex} className="flex h-10" data-id="element-334">
                    {['2021', '2022', '2023'].map(year => <div key={`${rowIndex}-${year}`} className="flex-1 border border-white relative" style={{
                  backgroundColor: getHeatmapColor(row[year])
                }} data-id="element-335">
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-800" data-id="element-336">
                          {row[year]}%
                        </span>
                      </div>)}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6" data-id="element-337">
          <h2 className="text-lg font-semibold mb-4" data-id="element-338">Revenue Split</h2>
          <div className="h-80" data-id="element-339">
            <ResponsiveContainer width="100%" height="100%" data-id="element-340">
              <PieChart data-id="element-341">
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="value" label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-342">
                  {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-343" />)}
                </Pie>
                <Tooltip data-id="element-344" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
}